import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik, useFormikContext } from "formik";
import { useToast } from "hooks/useToast";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  useAddTeamMemberMutation,
  useGetTeamMembersQuery,
  useUpdateTeamMemberMutation,
} from "store/apis/teamSetting";
import { useGetRolesQuery } from "store/apis/roles";
import { CreateEditRoleModal } from "./ManageRoles/CreateEditRoleModal";
import { ForeignEntityAccountantAccess } from "./ForeignEntityAccountantAccess";
import { useModal } from "hooks/useModal";
import {
  ARCHIVED,
  FOREIGN_ENTITY_ACCOUNTANT,
  INVITED,
} from "constants/groupRequest";
import {
  ACCESS_TYPE_ALL_TASKS,
  ACCESS_TYPE_SELECTED_TASKS,
} from "constants/foreignEntityAccountant";
import * as Yup from "yup";
import { BackendError } from "types/utils/error";

const ROLE_DESCRIPTIONS: Record<string, string> = {
  SUPER_ADMIN: "Full access to the platform, can manage users",
  STANDARD: "Full access to the bookkeeping platform",
  VIEW_ONLY: "View only access to the entire platform",
};

const FOREIGN_ENTITY_ACCOUNTANT_DESCRIPTION =
  "For other subsidiary/parent team users. Limited access & different dashboard.";

const FOREIGN_ENTITY_ACCOUNTANT_ROLE_ID = process.env.PUBLIC_ACCOUNTANT_ROLE_ID;

type User = {
  email: string;
  name?: string;
  profile_id?: string;
  role_id?: string;
  uuid?: string;
  allowed_access?: boolean;
};

type FormValues = {
  name: string;
  email: string;
  role: string;
  accessType: string;
  taskIdList: string[];
};

type RoleOption = {
  value: string;
  label: React.ReactNode;
};

const validationSchema = (
  isUpdate: boolean,
  existingEmailStatus: Map<string, string>
) =>
  Yup.object({
    name: Yup.string(),
    email: isUpdate
      ? Yup.string()
      : Yup.string()
          .email("Enter a valid email")
          .required("Email is required")
          .test("email-taken", function (value) {
            const status =
              value && existingEmailStatus.get(value.toLowerCase());
            if (!status) {
              return true;
            }

            const message =
              status === INVITED
                ? "This user has already been invited"
                : "This user is already part of the team";

            return this.createError({ message });
          }),
    role: Yup.string().required("Role is required"),
  });

const RoleCombobox = ({
  roleOptions,
  createRoleModal,
}: {
  roleOptions: RoleOption[];
  createRoleModal: { open: () => void };
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const selectedOption =
    roleOptions.find((opt) => opt.value === values.role) || null;

  return (
    <Combobox
      label="Role"
      name="role"
      menuPortalTarget={document.body}
      placeholder="Select"
      options={roleOptions}
      required
      value={selectedOption}
      onChange={(opt: any) => setFieldValue("role", opt?.value || "")}
      styles={{
        menuList: (base) => ({
          ...base,
          maxHeight: "260px",
          overflowY: "auto",
        }),
      }}
      actions={
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            createRoleModal.open();
          }}
          className="all:unset t-cursor-pointer t-text-body t-text-purple hover:t-text-purple-70 t-py-0.5"
        >
          Create new role
        </button>
      }
    />
  );
};

export const InviteUpdateUserModal = ({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user?: User | null;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const createRoleModal = useModal();

  const [addTeamMember, { isLoading: adding }] = useAddTeamMemberMutation();
  const [updateTeamMember, { isLoading: updating }] =
    useUpdateTeamMemberMutation();

  const { data: roles = [] } = useGetRolesQuery(
    { groupId: groupId! },
    { skip: !groupId }
  );
  const { data: teamMembers } = useGetTeamMembersQuery(
    { groupId: groupId! },
    { skip: !groupId }
  );

  const isUpdate = Boolean(user);
  const isLoading = adding || updating;
  const isExistingForeignEntityAccountant =
    isUpdate && user?.role_id === FOREIGN_ENTITY_ACCOUNTANT_ROLE_ID;

  const existingEmailStatus = new Map(
    (teamMembers?.group_users || [])
      .filter(({ status }) => status !== ARCHIVED)
      .map(({ email, status }) => [email.toLowerCase(), status])
  );

  const roleOptions: RoleOption[] = roles.map((role) => ({
    value: role.uuid,
    label: (
      <div>
        <div className="t-text-body t-text-text-100">{role.name}</div>
        {ROLE_DESCRIPTIONS[role.role_type] && (
          <div className="t-text-body-sm t-text-text-30">
            {ROLE_DESCRIPTIONS[role.role_type]}
          </div>
        )}
      </div>
    ),
  }));

  if (FOREIGN_ENTITY_ACCOUNTANT_ROLE_ID) {
    roleOptions.push({
      value: FOREIGN_ENTITY_ACCOUNTANT_ROLE_ID,
      label: (
        <div>
          <div className="t-text-body t-text-text-100">
            {FOREIGN_ENTITY_ACCOUNTANT}
          </div>
          <div className="t-text-body-sm t-text-text-30">
            {FOREIGN_ENTITY_ACCOUNTANT_DESCRIPTION}
          </div>
        </div>
      ),
    });
  }

  const handleSubmit = async (values: FormValues) => {
    const isForeignEntityAccountant =
      values.role === FOREIGN_ENTITY_ACCOUNTANT_ROLE_ID;
    const hasAllTaskAccess = values.accessType === ACCESS_TYPE_ALL_TASKS;

    let roleAccessBody: {
      group_role_id?: string;
      all_access_allowed?: boolean;
      task_id_list?: string[];
    } = { group_role_id: values.role };

    if (isForeignEntityAccountant) {
      roleAccessBody = hasAllTaskAccess
        ? { all_access_allowed: true }
        : { task_id_list: values.taskIdList };
    }

    try {
      if (isUpdate && user) {
        await updateTeamMember({
          id: groupId!,
          body: {
            email: user.email,
            name: values.name || undefined,
            profile_id: user.profile_id,
            ...roleAccessBody,
          },
        }).unwrap();
        successToast({ message: "User updated successfully" });
      } else {
        await addTeamMember({
          id: groupId!,
          body: {
            email: values.email,
            ...roleAccessBody,
          },
        }).unwrap();
        successToast({ message: "Invite sent successfully" });
      }
      onClose();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message || "Something went wrong",
      });
    }
  };

  return (
    <>
      <Modal.Root open={open} onOpenChange={onClose}>
        <Modal.Content useCustomOverlay>
          <Modal.Header>
            <Modal.Title>
              {isUpdate ? "Update user" : "Invite user"}
            </Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Formik
            initialValues={{
              name: user?.name || "",
              email: user?.email || "",
              role: user?.role_id || "",
              accessType: user?.allowed_access
                ? ACCESS_TYPE_ALL_TASKS
                : ACCESS_TYPE_SELECTED_TASKS,
              taskIdList: [],
            }}
            validationSchema={validationSchema(isUpdate, existingEmailStatus)}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isValid, values }) => (
              <Form className="all:unset">
                <Modal.Body className="t-flex t-flex-col t-gap-4">
                  <TextInput
                    label="User name"
                    name="name"
                    placeholder="Enter name"
                  />
                  <TextInput
                    label="Email Id"
                    name="email"
                    placeholder="Enter email"
                    disabled={isUpdate}
                    required={!isUpdate}
                  />
                  <RoleCombobox
                    roleOptions={roleOptions}
                    createRoleModal={createRoleModal}
                  />
                  {values.role === FOREIGN_ENTITY_ACCOUNTANT_ROLE_ID && (
                    <ForeignEntityAccountantAccess
                      groupId={groupId!}
                      providerId={
                        isExistingForeignEntityAccountant
                          ? user?.uuid
                          : undefined
                      }
                    />
                  )}
                </Modal.Body>
                <Modal.Footer className="t-flex t-justify-end t-gap-3">
                  {isUpdate && (
                    <Button type="button" onClick={onClose}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    customType="primary"
                    type="submit"
                    isLoading={isLoading}
                    disabled={isLoading || !isValid}
                  >
                    {isUpdate ? "Update" : "Invite"}
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </Modal.Content>
      </Modal.Root>
      <CreateEditRoleModal
        open={createRoleModal.isOpen}
        onClose={createRoleModal.close}
      />
    </>
  );
};
